import { FoxIconCheck, FoxIconCopyBold2 } from 'aui-biz-icons';
import classNames from 'clsx';
// eslint-disable-next-line you-dont-need-lodash-underscore/is-string
import isString from 'lodash-es/isString';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MarkdownSafeMdxViewerLocaleContext } from '../MarkdownSafeMdxViewer/MarkdownSafeMdxViewerLocaleContext';
import { copyTo } from '../utils/clipboard';
import { formatJSONObjectString } from './formatJSONObjectString';
import './highlightjs/github-dark.less';
import './highlightjs/google-light.less';
import styles from './index.module.less';
import {
  hasRegisteredLanguage,
  highlightCodeByLanguage,
  loadLang,
  transformLanguage,
  updateHighLightCode,
} from './LangManager';
// import { highLightInWorker } from './LangManagerWorkerEntry';
import { ClientOnly } from 'remix-utils/client-only';
import { formatMessageWithLocale } from '../../locales';
import VirtualizedCodeBlock from './VirtualizedCodeBlock';

export { formatJSONObjectString };

const highLightLimitSize = 30000;
// Threshold for when to use virtualized rendering
const VIRTUALIZATION_THRESHOLD = 50000; // 50KB
// Maximum characters to render at once in virtualized mode
const RENDER_CHUNK_SIZE = 5000; // 5KB

const CodeBlock = ({
  code,
  language = 'json',
  highlightTheme,
  theme,
  onlyCode = true,
  className,
}: {
  code: string;
  language?: string;
  theme?: string;
  highlightTheme?: string;
  onlyCode?: boolean;
  className?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<any>(null);
  const lang = transformLanguage(language);
  const locale = React.useContext(MarkdownSafeMdxViewerLocaleContext);
  const highLightRef = useRef(true);
  const preHighlightCodeLangRef = React.useRef<
    | {
        code: string;
        lang: string;
      }
    | undefined
  >(undefined);

  // State for virtualized rendering
  const [visibleCode, setVisibleCode] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadLang(lang);
  }, [lang]);

  // Determine if we should use virtualized rendering
  const shouldVirtualize = code.length > VIRTUALIZATION_THRESHOLD;

  const [highLightValue, setHighLightCode] = useState(() => {
    if (code.length > highLightLimitSize) {
      preHighlightCodeLangRef.current = undefined;
      highLightRef.current = false;
      setVisibleCode(code);
      return code;
    } else {
      const hasRegistered = hasRegisteredLanguage(lang);
      if (hasRegistered) {
        highLightRef.current = true;
        preHighlightCodeLangRef.current = {
          lang,
          code,
        };
        setVisibleCode(code);
        return highlightCodeByLanguage(lang, code);
      }
    }
    setVisibleCode(code);
    return '';
  });

  useLayoutEffect(() => {
    if (shouldVirtualize) {
      highLightRef.current = false;
      setVisibleCode(code);
    } else if (code.length > highLightLimitSize) {
      highLightRef.current = false;
      setHighLightCode(code);
      setVisibleCode(code);
    } else {
      if (preHighlightCodeLangRef.current) {
        const { code: tempCode, lang: tempLang } = preHighlightCodeLangRef.current;
        if (tempCode === code && tempLang === lang) {
          setVisibleCode(code);
          return;
        }
      }
      highLightRef.current = true;
      updateHighLightCode(lang, code, setHighLightCode);
      setVisibleCode(code);
      preHighlightCodeLangRef.current = {
        lang,
        code,
      };
    }
  }, [code, lang, shouldVirtualize]);

  const [preRef, setPreRef] = useState<HTMLPreElement | null>(null);
  useLayoutEffect(() => {
    preRef?.scrollTo({
      top: 0,
      left: 0,
    });
  }, [preRef, highLightValue]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const themeClass = useMemo(() => {
    let result = 'theme-github-dark';
    if (highlightTheme) {
      return `theme-${highlightTheme}`;
    }
    switch (theme) {
      case 'lightbrown':
      case 'light':
        result = 'theme-base16-google-light';
        break;
      default:
    }
    return result;
  }, [highlightTheme, theme]);

  const currentTheme = React.useMemo(() => {
    return themeClass.includes('dark') ? 'dark' : 'light';
  }, [themeClass]);

  if (!isString(code)) {
    return null;
  }

  // Use visibleCode instead of slicing here
  const codeElement = shouldVirtualize ? (
    <VirtualizedCodeBlock
      code={code}
      className={classNames(themeClass, styles['code-block'])}
      chunkSize={RENDER_CHUNK_SIZE}
      currentTheme={currentTheme}
    />
  ) : highLightRef.current ? (
    <code
      className={classNames(styles.code, 'hljs', 'hljs-' + language)}
      dangerouslySetInnerHTML={{ __html: visibleCode ? highLightValue : '' }}
    />
  ) : (
    <>{visibleCode}</>
  );

  const errorMsg = formatMessageWithLocale(locale, { id: 'overView.failToCopied' });
  const successMsg = formatMessageWithLocale(locale, { id: 'overView.isCopied' });

  if (!onlyCode) {
    const icon = (
      <>
        {!copied ? (
          <FoxIconCopyBold2
            className={styles['copy-icon']}
            onClick={(e) => {
              e.stopPropagation();
              copyTo(code || '', {
                errorMsg,
                successMsg,
              });
              setCopied(true);
              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }
              timerRef.current = setTimeout(() => {
                setCopied(false);
              }, 1000);
            }}
            size={16}
          />
        ) : (
          <FoxIconCheck
            className={classNames(styles['copy-icon'], styles['check-icon'])}
            size={16}
          />
        )}
      </>
    );

    return (
      <div
        ref={containerRef}
        className={classNames(
          styles.code,
          'relative',
          styles.container,
          {
            [styles['container-dark']]: currentTheme === 'dark',
            [styles['container-light']]: currentTheme === 'light',
          },
          className,
        )}
      >
        <ClientOnly>
          {() => {
            return code ? icon : null;
          }}
        </ClientOnly>
        {shouldVirtualize ? (
          codeElement
        ) : (
          <pre
            ref={setPreRef}
            className={classNames(
              themeClass,
              {
                [(styles as any)['code-block']]: true,
                [(styles as any)['code-block-no-highlight']]: !highLightRef.current,
              },
              'code-block-custom',
            )}
            style={{ '--markdown-x-component-gap': '0px' } as any}
          >
            {codeElement}
          </pre>
        )}
      </div>
    );
  }

  return (
    <>
      {shouldVirtualize ? (
        codeElement
      ) : (
        <pre
          ref={setPreRef}
          className={classNames(
            themeClass,
            styles['code-block'],
            'code-block-custom',
            styles.container,
            className,
          )}
          style={{ '--markdown-x-component-gap': '0px' } as any}
        >
          {codeElement}
        </pre>
      )}
    </>
  );
};

export default CodeBlock;
